import type { MutableRefObject } from 'react';
import { useCallback, useRef } from 'react';
import { matchPath, useHistory } from 'react-router-dom';

/**
 * Return a function that navigate to the matched path given the current URL.
 * NOTE: this is needed due to current structure of the project page, where the routes are randomly nested.
 * This approach should be avoided in the future.
 */
export const useNavigateToParentPage = (parentPagePaths: string[]) => {
  const pathsRef = useRef<string[]>(parentPagePaths);
  const history = useHistory();

  const getMatchedUrl = useGetMatchedUrl(pathsRef);

  return useCallback(
    (callback?: () => void) => {
      const targetPath = getMatchedUrl();

      if (!targetPath) {
        return;
      }

      if (callback && typeof callback === 'function') {
        callback();
      }
      history.push(targetPath);
    },
    [getMatchedUrl, history]
  );
};

const useGetMatchedUrl = (pathsRef: MutableRefObject<string[]>) => {
  const history = useHistory();

  return useCallback(() => {
    for (const path of pathsRef.current) {
      // https://v5.reactrouter.com/web/api/matchPath/props
      const match = matchPath(history.location.pathname, { path });
      if (match) {
        return match.url;
      }
    }
  }, [history.location.pathname, pathsRef]);
};
